/**
 * Générateur — Calculer le périmètre d'un disque
 * Attendu ID : 641 | Niveau : 6ème | Domaine : Grandeurs et mesures — Les longueurs
 *
 * Types générés : qcm_unique, texte, relier, tableau_vf, texte_trou_saisie
 * Labels de référence :
 *   - Calculer la longueur d'un cercle (rayon ou diamètre donné)
 *   - Calculer le périmètre d'un disque en contexte
 *   - Estimer un ordre de grandeur de périmètre
 * Variantes estimées : ~500+
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── SVG cercle avec rayon ou diamètre annoté ───

const buildCercleSvg = (val, isRayon, unite) => {
    const W = 180, H = 180, cx = 90, cy = 90, r = 65;
    const label = isRayon ? `R = ${fmtFr(val)} ${unite}` : `D = ${fmtFr(val)} ${unite}`;
    let annotation;
    if (isRayon) {
        // Trait du centre au bord droit + label
        annotation = `<line x1="${cx}" y1="${cy}" x2="${cx + r}" y2="${cy}" stroke="#fb923c" stroke-width="2" stroke-dasharray="4,3"/>`
            + `<circle cx="${cx}" cy="${cy}" r="3" fill="#fb923c"/>`
            + `<text x="${cx + r / 2}" y="${cy - 8}" text-anchor="middle" font-size="12" fill="#fb923c" font-family="system-ui,sans-serif">${label}</text>`;
    } else {
        // Trait de gauche à droite passant par le centre + label
        annotation = `<line x1="${cx - r}" y1="${cy}" x2="${cx + r}" y2="${cy}" stroke="#fb923c" stroke-width="2" stroke-dasharray="4,3"/>`
            + `<text x="${cx}" y="${cy - 8}" text-anchor="middle" font-size="12" fill="#fb923c" font-family="system-ui,sans-serif">${label}</text>`;
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">`
        + `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="currentColor" stroke-width="2.5"/>`
        + annotation
        + `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const cercleSvgElement = (val, isRayon, unite) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: buildCercleSvg(val, isRayon, unite), maxWidth: 'compact' } }],
});

// ─── Constantes ───
const ATTENDU_ID = 641;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALCUL = 'Calculer la longueur d\'un cercle (rayon ou diamètre donné)';
const L_CONTEXTE = 'Calculer le périmètre d\'un disque en contexte';

// ─── Helpers spécifiques ───
const arrDix = (n) => Math.round(n * 10) / 10;
const fmtFr = (n) => String(n).replace('.', ',');

// Vérifie que toutes les valeurs d'un tableau sont distinctes
const toutDistinct = (arr) => new Set(arr.map(String)).size === arr.length;

// ─── Pools de données ───
const PRENOMS_F = [
    { nom: 'Léa', fem: true }, { nom: 'Emma', fem: true },
    { nom: 'Jade', fem: true }, { nom: 'Louise', fem: true },
    { nom: 'Alice', fem: true }, { nom: 'Chloé', fem: true },
];
const PRENOMS_M = [
    { nom: 'Tom', fem: false }, { nom: 'Hugo', fem: false },
    { nom: 'Lucas', fem: false }, { nom: 'Nathan', fem: false },
    { nom: 'Raphaël', fem: false }, { nom: 'Louis', fem: false },
];
const PRENOMS = [...PRENOMS_F, ...PRENOMS_M];

const CONTEXTES = [
    { tpl: (p, val, u, pronom) => `${p.nom} fait le tour d'un bassin circulaire de {donnee} = ${fmtFr(val)} ${u}. Quelle distance ${pronom === 'Elle' ? 'parcourt-elle' : 'parcourt-il'} ? Arrondis au <strong>dixième</strong> de ${u}.`, unite: 'm', minR: 2, maxR: 15 },
    { tpl: (p, val, u, pronom) => `Une roue de vélo a un {donnee} = ${fmtFr(val)} ${u}. Quelle distance parcourt-on en un tour de roue ? Arrondis au <strong>dixième</strong> de ${u}.`, unite: 'cm', minR: 25, maxR: 40 },
    { tpl: (p, val, u, pronom) => `Un manège a un {donnee} = ${fmtFr(val)} ${u}. Quelle distance parcourt ${p.nom} en un tour ? Arrondis au <strong>dixième</strong> de ${u}.`, unite: 'm', minR: 3, maxR: 10 },
    { tpl: (p, val, u, pronom) => `${p.nom} veut entourer un gâteau circulaire de {donnee} = ${fmtFr(val)} ${u} avec un ruban. Quelle longueur de ruban faut-il ? Arrondis au <strong>dixième</strong> de ${u}.`, unite: 'cm', minR: 8, maxR: 18 },
    { tpl: (p, val, u, pronom) => `Un trampoline a un {donnee} = ${fmtFr(val)} ${u}. Calcule la longueur de son bord extérieur, arrondie au <strong>dixième</strong> de ${u}.`, unite: 'm', minR: 1, maxR: 3 },
];

// ─── Générateurs de pièges numériques ───
const genPiegesRayon = (R) => {
    const vraiP = arrDix(2 * Math.PI * R);
    const D = 2 * R;
    return {
        correct: vraiP,
        pieges: [
            arrDix(Math.PI * R),            // oubli du facteur 2 → moitié
            arrDix(2 * D),                  // oubli de π → juste le double du diamètre
            arrDix(3 * D),                  // π ≈ 3 grossier
        ],
    };
};

const genPiegesDiametre = (D) => {
    const vraiP = arrDix(Math.PI * D);
    const R = D / 2;
    return {
        correct: vraiP,
        pieges: [
            arrDix(2 * Math.PI * D),       // facteur 2 en trop → double
            arrDix(Math.PI * R),            // oubli du facteur 2 en passant par R → moitié
            arrDix(2 * D),                  // oubli de π → juste le double du diamètre
        ],
    };
};

// Tire une valeur et vérifie les contraintes
const tirerValeur = (isRayon, min, max) => {
    for (let attempt = 0; attempt < 50; attempt++) {
        // Entier ou 1 décimale
        const val = Math.random() < 0.6
            ? randInt(min, max)
            : Math.round((min + Math.random() * (max - min)) * 10) / 10;

        const result = isRayon ? genPiegesRayon(val) : genPiegesDiametre(val);

        // Vérifier P ≥ 5 et toutes valeurs distinctes
        if (result.correct >= 5 && toutDistinct([result.correct, ...result.pieges])) {
            return { val, ...result };
        }
    }
    // Fallback sûr
    const val = isRayon ? 7 : 14;
    return { val, ...(isRayon ? genPiegesRayon(val) : genPiegesDiametre(val)) };
};

// ─── Générateurs par type ───

const genCalculQcm = () => {
    const isRayon = Math.random() < 0.5;
    const { val, correct, pieges } = tirerValeur(isRayon, isRayon ? 2 : 4, isRayon ? 50 : 100);
    const unite = 'cm';
    const donnee = isRayon ? 'rayon R' : 'diamètre D';

    const texte = `<p>Un disque a un ${donnee} = ${fmtFr(val)} ${unite}. Quel est son périmètre arrondi au <strong>dixième</strong> de ${unite}\u00a0? Utilise la touche π de ta calculatrice.</p>{{element:0}}`;

    const correctStr = `${fmtFr(correct)} ${unite}`;
    const choix = [correctStr, ...pieges.map(p => `${fmtFr(p)} ${unite}`)];

    const payload = makeQcm(choix, correctStr, texte, { ...opts(L_CALCUL), elements_additionnels: cercleSvgElement(val, isRayon, unite) });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen) :
    // les clés sont en π exact, l'élève calcule avec la touche π.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_perimetre';
    return payload;
};

const genCalculSaisie = () => {
    const isRayon = Math.random() < 0.5;
    const { val, correct } = tirerValeur(isRayon, isRayon ? 2 : 4, isRayon ? 50 : 100);
    const unite = 'cm';
    const donnee = isRayon ? 'rayon R' : 'diamètre D';

    const texte = `<p>Calcule le périmètre d'un disque de ${donnee} = ${fmtFr(val)} ${unite}. Arrondis au <strong>dixième</strong> de ${unite}. Utilise la touche π de ta calculatrice.</p>{{element:0}}`;

    const payload = makeSaisie(fmtFr(correct), texte, { ...opts(L_CALCUL), suffixe: unite, elements_additionnels: cercleSvgElement(val, isRayon, unite) });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen) :
    // les clés sont en π exact, l'élève calcule avec la touche π.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_perimetre';
    return payload;
};

const genContexteSaisie = () => {
    const prenom = randomChoice(PRENOMS);
    const pronom = prenom.fem ? 'Elle' : 'Il';
    const ctx = randomChoice(CONTEXTES);
    const isRayon = Math.random() < 0.5;
    const minV = isRayon ? ctx.minR : ctx.minR * 2;
    const maxV = isRayon ? ctx.maxR : ctx.maxR * 2;
    const { val, correct } = tirerValeur(isRayon, minV, maxV);

    const donneeLabel = isRayon ? 'rayon' : 'diamètre';
    const enonce = ctx.tpl(prenom, val, ctx.unite, pronom).replace('{donnee}', donneeLabel);
    const texte = `<p>${enonce} Utilise la touche π de ta calculatrice.</p>{{element:0}}`;

    const payload = makeSaisie(fmtFr(correct), texte, { ...opts(L_CONTEXTE), suffixe: ctx.unite, elements_additionnels: cercleSvgElement(val, isRayon, ctx.unite) });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen) :
    // les clés sont en π exact, l'élève calcule avec la touche π.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_perimetre';
    return payload;
};

const genCalculRelier = () => {
    // 4 disques : 2 avec rayon, 2 avec diamètre
    const pairs = [];
    const perimetres = new Set();

    for (let i = 0; i < 4; i++) {
        const isR = i < 2;
        for (let attempt = 0; attempt < 50; attempt++) {
            const val = randInt(isR ? 3 : 6, isR ? 20 : 40);
            const p = isR ? arrDix(2 * Math.PI * val) : arrDix(Math.PI * val);
            if (p >= 5 && !perimetres.has(p)) {
                perimetres.add(p);
                const source = isR ? `Rayon R = ${val} cm` : `Diamètre D = ${val} cm`;
                pairs.push({ source, target: `${fmtFr(p)} cm` });
                break;
            }
        }
    }

    // Fallback si pas assez de paires
    while (pairs.length < 4) {
        const val = 5 + pairs.length * 3;
        const p = arrDix(Math.PI * val * 2);
        pairs.push({ source: `Rayon R = ${val} cm`, target: `${fmtFr(p)} cm` });
    }

    const texte = '<p>Relie chaque objet circulaire à son périmètre arrondi au <strong>dixième</strong>. Utilise la touche π de ta calculatrice. <em>R = rayon, D = diamètre.</em></p>';

    const payload = makeRelier(pairs, texte, opts(L_CALCUL));
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen) :
    // les clés sont en π exact, l'élève calcule avec la touche π.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_perimetre';
    return payload;
};

const genCalculVF = () => {
    const lignes = [];
    const bonnesReponses = [];

    // 2 affirmations correctes
    for (let i = 0; i < 2; i++) {
        const isR = i === 0;
        const val = randInt(isR ? 3 : 6, isR ? 20 : 40);
        const p = isR ? arrDix(2 * Math.PI * val) : arrDix(Math.PI * val);
        const donnee = isR ? `rayon R = ${val} cm` : `diamètre D = ${val} cm`;
        lignes.push(`Un disque de ${donnee} a un périmètre d'environ ${fmtFr(p)} cm`);
        bonnesReponses.push(0); // Correct
    }

    // 2 affirmations incorrectes (avec erreurs typiques)
    // Erreur 1 : oubli du facteur 2 (rayon)
    const r1 = randInt(4, 15);
    const pFaux1 = arrDix(Math.PI * r1); // oubli du 2
    lignes.push(`Un disque de rayon R = ${r1} cm a un périmètre d'environ ${fmtFr(pFaux1)} cm`);
    bonnesReponses.push(1); // Incorrect

    // Erreur 2 : facteur 2 en trop (diamètre)
    const d2 = randInt(8, 30);
    const pFaux2 = arrDix(2 * Math.PI * d2); // facteur 2 en trop
    lignes.push(`Un disque de diamètre D = ${d2} cm a un périmètre d'environ ${fmtFr(pFaux2)} cm`);
    bonnesReponses.push(1); // Incorrect

    // Mélanger les lignes
    const indices = shuffle([0, 1, 2, 3]);
    const lignesMel = indices.map(i => lignes[i]);
    const brMel = indices.map(i => bonnesReponses[i]);

    const texte = '<p>Pour chaque calcul, indique s\'il est correct ou incorrect.</p>{{element:0}}';

    const payload = makeVraiFaux(lignesMel, brMel, texte, { ...opts(L_CALCUL), colonnes: ['Correct', 'Incorrect'] });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen) :
    // les clés sont en π exact, l'élève calcule avec la touche π.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_perimetre';
    return payload;
};

const genCalculTrouSaisie = () => {
    const isRayon = Math.random() < 0.5;
    const val = randInt(isRayon ? 2 : 4, isRayon ? 30 : 60);
    const p = isRayon ? arrDix(2 * Math.PI * val) : arrDix(Math.PI * val);

    let phrase;
    if (isRayon) {
        phrase = `Un disque a un rayon R = ${val} cm. Son périmètre est : P = 2 × π × {{${val}}} ≈ {{${fmtFr(p)}}} cm.`;
    } else {
        phrase = `Un disque a un diamètre D = ${val} cm. Son périmètre est : P = π × {{${val}}} ≈ {{${fmtFr(p)}}} cm.`;
    }

    const texte = '<p>Complète le calcul du périmètre. Les valeurs avec ≈ sont arrondies au <strong>dixième</strong>. Utilise la touche π de ta calculatrice.</p>{{element:0}}';

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALCUL), mode: 'saisie', elements_additionnels: cercleSvgElement(val, isRayon, 'cm') });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen) :
    // les clés sont en π exact, l'élève calcule avec la touche π.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'calcul_perimetre';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    calcul_perimetre_qcm_unique: genCalculQcm,
    calcul_perimetre_texte: genCalculSaisie,
    calcul_perimetre_contexte_texte: genContexteSaisie,
    calcul_perimetre_relier: genCalculRelier,
    calcul_perimetre_tableau_vf: genCalculVF,
    calcul_perimetre_texte_trou_saisie: genCalculTrouSaisie,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    contexte_texte: 'Saisie contexte',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul_perimetre', label: L_CALCUL, formatsList: buildFormats('calcul_perimetre') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
